import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Button } from "@/components/ui/Button";
import { formatAmount } from "@/utils/formatAmount";
import { useCoefficientSets, useJobs, useStartEstimateJob, useTaskPolling } from "../hooks";
import { deadlineLabel, deadlineToneClass } from "../deadline";
import type { EstimateAnalytics, Variant } from "../types";

/** Skrót wariantu wartości przetargowej, którym opisujemy kwotę na liście. */
const VARIANT_SHORT: Record<Variant, string> = {
  min: "min.",
  avg: "śr.",
  max: "maks.",
};

function fmtMoney(v?: string): string {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  // formatAmount always groups thousands (pl-PL locale skips grouping for 4-digit numbers).
  return formatAmount(n) + " zł";
}

function fmtMeters(v?: string | null): string {
  if (v == null) return "";
  const n = Number(v);
  return Number.isNaN(n) ? "" : formatAmount(n);
}

/** Compact route summary for a job row, e.g. "trasa 242,00 mb (wykop 200,00 · przewiert 42,00)". */
function routeSummary(a?: EstimateAnalytics | Record<string, never>): string {
  const an = a as EstimateAnalytics | undefined;
  if (!an?.route_length_m) return "";
  const parts: string[] = [];
  if (an.route_open_trench_m) parts.push(`wykop ${fmtMeters(an.route_open_trench_m)}`);
  if (an.route_bore_m) parts.push(`przewiert ${fmtMeters(an.route_bore_m)}`);
  const head = `trasa ${fmtMeters(an.route_length_m)} mb`;
  return parts.length ? `${head} (${parts.join(" · ")})` : head;
}

export function EstimateUploadPage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [coeffId, setCoeffId] = useState<string>("");
  // Termin złożenia + link do przetargu — na razie z ręki (docelowo z importu ZE).
  const [deadline, setDeadline] = useState<string>("");
  const [tenderUrl, setTenderUrl] = useState<string>("");
  const [taskId, setTaskId] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const coeffSets = useCoefficientSets();
  const jobs = useJobs();
  const start = useStartEstimateJob();
  const task = useTaskPolling(taskId);
  const logRef = useRef<HTMLPreElement>(null);

  useEffect(() => {
    if (coeffSets.data && !coeffId) {
      setCoeffId(coeffSets.data.find((c) => c.is_default)?.id ?? coeffSets.data[0]?.id ?? "");
    }
  }, [coeffSets.data, coeffId]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [task.data?.log]);

  const status = task.data?.status;
  const done = status === "success";

  function submit() {
    start.mutate(
      {
        files,
        coefficient_set: coeffId || undefined,
        offer_deadline: deadline || undefined,
        tender_url: tenderUrl.trim() || undefined,
      },
      {
        onSuccess: (res) => {
          setTaskId(res.task_id);
          setJobId(res.job_id);
        },
      },
    );
  }

  const recentJobs = useMemo(() => jobs.data ?? [], [jobs.data]);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">Wycena kosztorysu nakładczego</h1>
        <p className="text-sm text-[var(--color-text-secondary)] mt-1">
          Wgraj PDF-y (lub ZIP-y z PDF-ami). AI wskaże, które strony to kosztorys nakładczy, wyciągnie
          nakłady R/M/S i wyceni metodą szczegółową wg stawek SEKOCENBUD (min / średnia / maks).
        </p>
      </div>

      {!taskId && (
        <div className="card space-y-4 p-4">
          <FileDropZone files={files} onFilesChange={setFiles} multiple accept=".pdf,.zip" />
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm text-[var(--color-text-secondary)] space-y-1">
              <span>Termin złożenia oferty</span>
              <input
                type="datetime-local"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-sm px-2 py-1 text-[var(--color-text-primary)]"
              />
            </label>
            <label className="text-sm text-[var(--color-text-secondary)] space-y-1">
              <span>Link do przetargu</span>
              <input
                type="url"
                value={tenderUrl}
                onChange={(e) => setTenderUrl(e.target.value)}
                placeholder="https://platformazakupowa.pl/…"
                className="w-full border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-sm px-2 py-1 text-[var(--color-text-primary)]"
              />
            </label>
          </div>
          <p className="text-xs text-[var(--color-text-muted)]">
            Oba pola są opcjonalne — możesz je uzupełnić lub poprawić później w wyniku wyceny.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm text-[var(--color-text-secondary)]">Współczynniki:</label>
            <select
              value={coeffId}
              onChange={(e) => setCoeffId(e.target.value)}
              className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-sm px-2 py-1"
            >
              {(coeffSets.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} — Kp {c.kp_pct}% / Z {c.z_pct}% / Kz {c.kz_pct}%
                </option>
              ))}
            </select>
            <Button onClick={submit} disabled={!files.length || start.isPending} loading={start.isPending}>
              Wyceń ({files.length})
            </Button>
          </div>
          {start.isError && (
            <p className="text-sm text-red-500">Nie udało się rozpocząć analizy. Sprawdź pliki i spróbuj ponownie.</p>
          )}
        </div>
      )}

      {taskId && (
        <div className="card space-y-3 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[var(--color-text-primary)]">
              {done ? "Analiza zakończona" : status === "failure" ? "Błąd analizy" : "Analiza w toku…"}
            </span>
            {done && jobId && <Button onClick={() => navigate(`/estimating/${jobId}`)}>Zobacz wynik</Button>}
          </div>
          <pre
            ref={logRef}
            className="text-xs font-mono bg-[var(--color-bg-elevated)] p-3 max-h-72 overflow-auto whitespace-pre-wrap text-[var(--color-text-secondary)]"
          >
            {task.data?.log || "Uruchamianie…"}
          </pre>
          {status === "failure" && (
            <p className="text-xs text-red-500">{task.data?.error?.slice(0, 500)}</p>
          )}
          <button
            className="text-xs text-[var(--color-text-secondary)] underline hover:text-[var(--color-primary)]"
            onClick={() => {
              setTaskId(null);
              setJobId(null);
              setFiles([]);
              setDeadline("");
              setTenderUrl("");
            }}
          >
            Nowa wycena
          </button>
        </div>
      )}

      <div>
        <h2 className="section-label mb-2">Ostatnie wyceny</h2>
        <ul className="card divide-y divide-[var(--color-border)]">
          {recentJobs.length === 0 && (
            <li className="p-3 text-sm text-[var(--color-text-secondary)]">Brak zapisanych wycen.</li>
          )}
          {recentJobs.map((j) => {
            const route = routeSummary(j.analytics);
            return (
              <li key={j.id} className="p-3 flex items-center justify-between gap-3 text-sm hover:bg-[var(--color-bg-base)]">
                <div className="min-w-0">
                  <Link to={`/estimating/${j.id}`} className="text-[var(--color-primary)] hover:underline">
                    {new Date(j.created_at).toLocaleString("pl-PL")} — {j.estimate_count} kosztorys(ów)
                  </Link>
                  {(j.title || route) && (
                    <div
                      className="text-xs text-[var(--color-text-secondary)] truncate"
                      title={j.title || undefined}
                    >
                      {j.title && <span className="text-[var(--color-text-primary)]">{j.title}</span>}
                      {j.title && route && " · "}
                      {route}
                    </div>
                  )}
                  {(j.offer_deadline || j.tender_url) && (
                    <div className="text-xs flex items-center gap-2 flex-wrap">
                      {j.offer_deadline && (
                        <span
                          className={deadlineToneClass(j.offer_deadline) || "text-[var(--color-text-secondary)]"}
                          title="Termin złożenia oferty"
                        >
                          termin: {deadlineLabel(j.offer_deadline)}
                        </span>
                      )}
                      {j.tender_url && (
                        <a
                          href={j.tender_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[var(--color-primary)] hover:underline"
                          title={j.tender_url}
                        >
                          link do przetargu ↗
                        </a>
                      )}
                    </div>
                  )}
                  <div className="text-[11px] text-[var(--color-text-muted)] truncate">
                    autor: {j.created_by_name}
                    {j.is_shared_with_me && (
                      <span className="ml-1 text-blue-600">· udostępnione Tobie</span>
                    )}
                    {j.shared_with_names.length > 0 && (
                      <span className="ml-1">
                        · udostępnione: {j.shared_with_names.join(", ")}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-[var(--color-text-secondary)] whitespace-nowrap">
                  <span
                    className={
                      // Wynik przetargu (wygrany/przegrane/anulowane) jest stanem
                      // końcowym — wygrywa też kolorem.
                      j.outcome
                        ? j.outcome === "won"
                          ? "text-emerald-700 font-semibold"
                          : j.outcome === "lost"
                            ? "text-rose-700 font-semibold"
                            : "text-slate-600 font-semibold"
                        : j.is_abandoned
                        ? "text-slate-500 font-medium"
                        : j.status === "ready"
                          ? j.is_submitted
                            ? "text-sky-600 font-medium"
                            : j.is_completed
                              ? "text-emerald-600 font-medium"
                              : "text-amber-600 font-medium"
                          : j.status === "failed"
                            ? "text-red-600"
                            : undefined
                    }
                  >
                    {j.status_display}
                  </span>
                  {/* Kwota z wariantu złożonego jako oferta (min/śr/maks), nie zawsze średnia. */}
                  {j.totals_snapshot?.[j.bid_variant ?? "avg"] && (
                    <>
                      {" "}
                      · {VARIANT_SHORT[j.bid_variant ?? "avg"]}{" "}
                      {fmtMoney(j.totals_snapshot[j.bid_variant ?? "avg"]!.total)}
                    </>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
